//
//  SDKSupernova.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { AreaAssets } from "./areas/SDKAreaAssets";
import { AreaAutomation } from "./areas/SDKAreaAutomation";
import { AreaBrands } from "./areas/SDKAreaBrands";
import { AreaBulkOperations } from "./areas/SDKAreaBulkOperations";
import { AreaCodeComponents } from "./areas/SDKAreaCodeComponents";
import { AreaComponents } from "./areas/SDKAreaComponents";
import { AreaContext } from "./areas/SDKAreaContext";
import { AreaDataSources } from "./areas/SDKAreaDataSources";
import { AreaDesignSystems } from "./areas/SDKAreaDesignSystems";
import { AreaDocsAnalytics } from "./areas/SDKAreaDocsAnalytics";
import { AreaDocumentation } from "./areas/SDKAreaDocumentation";
import { AreaIntegrations } from "./areas/SDKAreaIntegrations";
import { AreaMCP } from "./areas/SDKAreaMCP";
import { AreaMe } from "./areas/SDKAreaMe";
import { AreaMessaging } from "./areas/SDKAreaMessaging";
import { AreaMultiplayer } from "./areas/SDKAreaMultiplayer";
import { AreaNetwork } from "./areas/SDKAreaNetwork";
import { AreaProjects } from "./areas/SDKAreaProjects";
import { AreaRawData } from "./areas/SDKAreaRawData";
import { AreaRelay } from "./areas/SDKAreaRelay";
import { AreaResources } from "./areas/SDKAreaResources";
import { AreaSandboxes } from "./areas/SDKAreaSanboxes";
import { AreaStorage } from "./areas/SDKAreaStorage";
import { AreaThreads } from "./areas/SDKAreaThreads";
import { AreaTokens } from "./areas/SDKAreaTokens";
import { AreaUsers } from "./areas/SDKAreaUsers";
import { AreaVersions } from "./areas/SDKAreaVersions";
import { AreaWorkspaceIntegrations } from "./areas/SDKAreaWorkspaceIntegrations";
import { AreaWorkspaces } from "./areas/SDKAreaWorkspaces";
import { Configuration } from "./data/SDKConfiguration";
import { DataBridge, } from "./data/SDKDataBridge";
import { DataCore } from "./data/SDKDataCore";
import { DataCoreCached } from "./data/SDKDataCoreCached";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Function Definition
/** Main Supernova.io SDK object. Use this to connect to your data instance and retrieve workspace / design system / version from which you can access all neccessary data. */
export class Supernova {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    /**
     * Create new Supernova.io SDK object.
     * @param apiKey API key to use for authentication
     * @param options Options for SDK
     * @param options.url URL of the Supernova.io instance to connect to. Defaults to production environment
     * @param options.requestHook Hook for observing requests
     * @param options.customFetchLib When provided, network requests will run through custom provided, fetch-like. Use in environments where fetch is not available or you want to provide your own implementation.
     * @param options.customProxyLib When provided, proxy will be used to route network requests.
     */
    constructor(apiKey, options) {
        var _a, _b, _c;
        this.requestHook = (_a = options === null || options === void 0 ? void 0 : options.requestHook) !== null && _a !== void 0 ? _a : null;
        this.apiKey = apiKey;
        this.apiUrl = (_b = options === null || options === void 0 ? void 0 : options.apiUrl) !== null && _b !== void 0 ? _b : Configuration.apiUrlForDefaultEnvironment();
        this.aiApiUrl =
            (_c = options === null || options === void 0 ? void 0 : options.aiApiUrl) !== null && _c !== void 0 ? _c : Configuration.aiApiUrlForDefaultEnvironment();
        this.apiVersion = Configuration.apiVersionForDefaultEnvironment();
        const dataBridge = new DataBridge({
            apiUrl: this.apiUrl,
            aiApiUrl: this.aiApiUrl,
            apiVersion: this.apiVersion,
            accessToken: this.apiKey,
            customFetchLib: options === null || options === void 0 ? void 0 : options.customFetchLib,
            customProxyLib: options === null || options === void 0 ? void 0 : options.customProxyLib,
            target: null,
            isPreview: options === null || options === void 0 ? void 0 : options.isPreview,
            jobId: options === null || options === void 0 ? void 0 : options.jobId,
            requestHook: this.requestHook,
            debugRequestObserver: options === null || options === void 0 ? void 0 : options.debugRequestObserver,
            debugResponseObserver: options === null || options === void 0 ? void 0 : options.debugResponseObserver,
        });
        this.dataCore = (options === null || options === void 0 ? void 0 : options.useDataCoreCached)
            ? new DataCoreCached(dataBridge)
            : new DataCore(dataBridge);
        this.workspaces = new AreaWorkspaces(this.dataCore);
        this.designSystems = new AreaDesignSystems(this.dataCore);
        this.versions = new AreaVersions(this.dataCore);
        this.brands = new AreaBrands(this.dataCore);
        this.me = new AreaMe(this.dataCore);
        this.tokens = new AreaTokens(this.dataCore);
        this.codeComponents = new AreaCodeComponents(this.dataCore);
        this.components = new AreaComponents(this.dataCore);
        this.assets = new AreaAssets(this.dataCore);
        this.resources = new AreaResources(this.dataCore);
        this.documentation = new AreaDocumentation(this.dataCore);
        this.docsAnalytics = new AreaDocsAnalytics(this.dataCore);
        this.automation = new AreaAutomation(this.dataCore);
        this.users = new AreaUsers(this.dataCore);
        this.integrations = new AreaIntegrations(this.dataCore);
        this.workspaceIntegrations = new AreaWorkspaceIntegrations(this.dataCore);
        this.dataSources = new AreaDataSources(this.dataCore);
        this.mcp = new AreaMCP(this.dataCore);
        this.bulkOperations = new AreaBulkOperations(this.dataCore);
        this.storage = new AreaStorage(this.dataCore);
        this.network = new AreaNetwork(this.dataCore);
        this.multiplayer = new AreaMultiplayer(this.dataCore);
        this.relay = new AreaRelay(this.dataCore);
        this.messaging = new AreaMessaging(this.dataCore);
        this.raw = new AreaRawData(this.dataCore);
        this.projects = new AreaProjects(this.dataCore);
        this.context = new AreaContext(this.dataCore);
        this.threads = new AreaThreads(this.dataCore);
        this.sandboxes = new AreaSandboxes(this.dataCore);
    }
    get apiKeySet() {
        var _a;
        return Boolean((_a = this.apiKey) === null || _a === void 0 ? void 0 : _a.length);
    }
}
